import { useDispatch } from 'stimulus-use'
import { Controller as BaseController } from 'stimulus'
import Rails from '@rails/ujs'
import I18n from '@app/javascript/i18n/index.js.erb'
import './index.scss'
import 'custom-event-polyfill'
import CSVFileValidator from 'csv-file-validator'
import emailValidator from '@app/javascript/controllers/mixins/emailValidator.js'

export class Controller extends BaseController {
  static targets = [
    'input',
    'error',
    'attachFile',
    'removeFile',
    'fileName',
    'fileRegion'
  ]

  static values = {
    maxFiles: Number,
    maxFilesize: Number,
    maxFilesizeString: String,
    acceptedFiles: Array,
    acceptedFileExts: Array,
    validateFileContent: Boolean,
    freeSpace: Number,
    directUploadUrl: String,
    fileValidationUrl: String,
    rollbackUrl: String,
    fileId: String
  }

  errors = {}
  files = {}
  /* This should be refactored, separating this json into another directory each file upload instance
   * define its own validation schema. This is a temporary solution to allow for the validation of the file content.
  */
  validationSchema = {
    headers: [
      {
        name: 'First Name*',
        inputName: 'firstName',
        required: true,
        requiredError: function (headerName, rowNumber, columnNumber) {
          return `${headerName} is required in the ${rowNumber} row / ${columnNumber} column`
        }
      },
      {
        name: 'Last Name*',
        inputName: 'lastName',
        required: true,
        requiredError: function (headerName, rowNumber, columnNumber) {
          return `${headerName} is required in the ${rowNumber} row / ${columnNumber} column`
        }
      },
      {
        name: 'Title/Position',
        inputName: 'titlePosition',
        required: false
      },
      {
        name: 'Email',
        inputName: 'email',
        required: false,
        validate: function (email) {
          return emailValidator(email, true)
        },
        validateError: function (headerName, rowNumber, columnNumber) {
          return `Please enter a valid ${headerName} on: ${rowNumber} row / ${columnNumber} column.`
        }
      },
      {
        name: 'Organization',
        inputName: 'organization',
        required: false
      },
      {
        name: 'Phone',
        inputName: 'phone',
        required: false
      },
      {
        name: 'Mobile',
        inputName: 'mobile',
        required: false
      },
      {
        name: 'Address',
        inputName: 'address',
        required: false
      },
      {
        name: 'Website',
        inputName: 'website',
        required: false
      },
      {
        name: 'About',
        inputName: 'about',
        required: false
      }
    ]
  }

  connect() {
    useDispatch(this)
    this.updateFile()
  }

  removeFile() {
    this.attachFileTarget.value = ''
    this.fileRegionTarget.innerHTML = ''
    this.updateFile()
    this.enableSubmit()

    const event = new CustomEvent('shared-upload-file-button:remove-file')
    window.dispatchEvent(event)
  }

  updateFile() {
    if (this.hasAttachFileTarget) {
      this.fileNameTarget.innerHTML = this.attachFileTarget.value ? this.attachFileTarget.value.replace(/^.*[\\/]/, '') : ''
      this.removeFileTarget.style.visibility = this.attachFileTarget.value ? 'visible' : 'hidden'
      const file = this.attachFileTarget.files[0]
      if (this.attachFileTarget.value) {
        this.validateFile(file, (valid) => {

        })
      }
    }
  }

  validateFile(file, onValidated) {
    const invalidMimetype = !this.acceptedFilesValue.includes(file.type)

    if (file.size > this.maxFilesizeValue) {
      this.removeFile()
      this.fileRegionTarget.innerHTML = I18n.t('upload_file.errors.file_size', { limit: this.maxFilesizeStringValue })
      return onValidated(false)
    }

    if (invalidMimetype) {
      this.removeFile()
      this.fileRegionTarget.innerHTML = I18n.t('upload_file.errors.mimetype', { accepted_types: this.acceptedFileExtsValue.join(', ') })
      return onValidated(false)
    }

    if (this.validateFileContentValue) {
      this.validateFileContent((invalidFileContent) => {
        if (invalidFileContent.length > 0) {
          this.removeFile()
          this.fileRegionTarget.innerHTML = `${invalidFileContent.map(error => error.message).join('<br/>')}`
          return onValidated(false)
        }
      })
    }

    if (this.fileValidationUrlValue) {
      Rails.ajax({
        type: 'POST',
        url: this.fileValidationUrlValue,
        data: $.param({
          filename: file.name
        }),
        success: (resp) => {
          const filenameTaken = !resp.valid_filename || Object.values(this.files).find(
            f => this.sansExtension(f.name) === this.sansExtension(file.name)
          )
          if (filenameTaken) {
            this.removeFile()
            this.fileRegionTarget.innerHTML = I18n.t('upload_file.errors.filename')
            return onValidated(false)
          }
          this.fileRegionTarget.innerHTML = ''
          return onValidated(true)
        }
      })
    } else {
      this.fileRegionTarget.innerHTML = ''
      return onValidated(true)
    }
  }

  validateFileContent(callback) {
    const file = this.attachFileTarget.files[0]
    const config = this.validationSchema

    CSVFileValidator(file, config)
      .then(csvData => {
        const event = new CustomEvent('shared-upload-file-button:validated-file', { detail: { file: { length: csvData.data.length } } })

        callback(csvData.inValidData)
        window.dispatchEvent(event)
      })
      .catch(err => {
        const error = [`Error validating file content: ${err}`]
        callback(error)
      })
  }

  validateFilename(event) {
    const filename = event.currentTarget.value;
    const id = this.fileIdValue;

    Rails.ajax({
      type: 'POST',
      url: this.fileValidationUrlValue,
      data: $.param({
        filename: filename,
        id: id
      }),
      success: (resp) => {
        const filenameTaken = !resp.valid_filename || Object.values(this.files).find(
          f => this.sansExtension(f.name) === this.sansExtension(file.name)
        )
        if (filenameTaken) {
          this.fileRegionTarget.innerHTML = I18n.t('edit_file.errors.filename')
          return true
        }
        this.fileRegionTarget.innerHTML = ''
      }
    })
  }

  enableSubmit() {
    const buttonSubmit = document.querySelector('button[type="submit"]')
    const inputSubmit = document.querySelector('input[type="submit"]')
    if (buttonSubmit) {
      buttonSubmit.disabled = false
    }
    if (inputSubmit) {
      inputSubmit.disabled = false
    }
  }
}
